/**
 * The home screen's bento grid.
 *
 * One CSS grid, one uniform gap, tiles of different widths — "a hero tile among small
 * ones like compartments in a Japanese lunchbox." The whole page reads as a single
 * rounded box of compartments because every card already shares the same chrome
 * (rounded-card / border / bg-surface from home/ui.tsx Card).
 *
 * How it stays clean:
 *   - Six columns on desktop, two on a tablet, one on a phone (a kitchen phone wants
 *     full-width cards, not slivers). Spans are named, not computed, so Tailwind keeps them.
 *   - The grid stretches every cell to its row's height (grid default), and each cell
 *     stretches its card to fill (`[&>*]:h-full`), so a row of a wide and a narrow tile
 *     lines up flush at top AND bottom — no ragged compartments.
 *   - Gap lives on the grid, never as per-card margins.
 */
import type { ReactNode } from 'react';

export function Bento({ children }: { children: ReactNode }) {
  // `grid-flow-dense` lets a tall tile sit in the grid while smaller tiles pack into the
  // gaps it leaves — so we can mix vertical and horizontal cards without leaving holes.
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6 lg:[grid-auto-flow:dense]">
      {children}
    </div>
  );
}

/**
 * Widths, phone → tablet → desktop (of 6). `tall` adds a second row on desktop for an
 * anchor tile whose card has the content to fill it (the money tile).
 */
type Size = 'small' | 'half' | 'wide' | 'full';

const SPAN: Record<Size, string> = {
  small: 'sm:col-span-1 lg:col-span-2', // a third of the width on desktop
  half: 'sm:col-span-2 lg:col-span-3', // half
  wide: 'sm:col-span-2 lg:col-span-4', // two thirds
  full: 'sm:col-span-2 lg:col-span-6', // the whole row
};

export function Cell({
  size = 'small',
  tall = false,
  children,
}: {
  size?: Size;
  tall?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={`${SPAN[size]} ${tall ? 'lg:row-span-2' : ''} [&>*]:h-full`}>{children}</div>
  );
}
